import { bootstrappedData } from 'utils/bootstrap';

const isProduction = process.env.NODE_ENV === 'production';

export default {
  isProduction,
  publicPath: '/',
  baseUrl: ((bootstrappedData?.host || (global as any).window?.location.origin) as string) ?? '',
  suggestionDefaultProjectColor: 'CCCCCC', // Was '6C7077' and mismatched with HourEditor's TimeSection.
  animation: {
    /** Value in seconds. */
    duration: 0.25,
    easing: [0.23, 1, 0.32, 1] as [number, number, number, number], // Duplicate in variables.sass
  },
  defaultHourRate: 15,
  defaultInternalHourRate: 0,
  apps: {
    // Mac
    memoryMacTrackerUrl: 'https://memory.timelyapp.com/download/mac',
    memoryMacTrackerSupportUrl: 'https://support.timelyapp.com/en/articles/8098512-memory-for-mac',
    // Windows
    memoryWindowsTrackerUrl: (newVersion: boolean) =>
      newVersion
        ? 'https://memory.timelyapp.com/download/windows'
        : 'https://memorywindowsapp.s3.eu-west-1.amazonaws.com/Memory.zip',
    memoryWindowsTrackerSupportUrl:
      'https://support.timelyapp.com/en/articles/9306193-memory-for-windows',
    // Linux
    memoryLinuxTrackerUrl: 'https://memory.timelyapp.com/download/linux',
    memoryLinuxTrackerSupportUrl: 'https://support.memory.ai/',
    // Chrome
    memoryChromeTrackerUrl:
      'https://chrome.google.com/webstore/detail/memory-for-chrome/imlnhbmpcfbnonoabmfkglmepnohkabn',
    memoryChromeTrackerSupportUrl:
      'https://support.memory.ai/en/articles/5959709-how-to-set-up-memory-for-chrome',
    // Desktop
    desktopAppWindowsUrl: 'https://app.timelyapp.com/download/win64',
    desktopAppMacUrl: 'https://app.timelyapp.com/download/mac',
    // Mobile
    mobileAppAndroidUrl: 'https://play.google.com/store/apps/details?id=com.timeapp.devlpmp&hl=en',
    mobileAppIOSUrl: 'https://itunes.apple.com/us/app/timely-time-tracking-app-to/id619462420?mt=8',
    // Google Sheets
    googleSheetsUrl: (resourceId: string) => `https://docs.google.com/spreadsheets/d/${resourceId}`,
  },
  publicReportLinkBackUrl:
    'https://www.timelyapp.com/?utm_source=timelyapp.com&utm_medium=public_report&utm_content=promo_button',
  videos: {
    aiTimeTracking: 'https://www.youtube.com/watch?v=6E-R7fBSv-I',
  },
  /** Timely-hosted video (R2 behind Cloudflare). Slots live under `/vod/<slot>/`; see tools/video/README.md. */
  mediaBaseUrl: 'https://media.timely.com',
  bookDemoUrl: {
    smallCompanies: {
      external: 'https://go.timelyapp.com/book-a-demo-a',
      embed: 'https://go.timelyapp.com/meetings/timelyteam/demo-timely-app-a?embed=true',
    },
    largeCompanies: {
      external: 'https://go.timelyapp.com/book-a-demo-b',
      embed: 'https://go.timelyapp.com/meetings/timelyteam/demo-timely-app-b?embed=true',
    },
  },
  survey: {
    generalFeedback: 'https://timely.typeform.com/to/KZ6xuN',
    aiSuggestions: 'https://form.typeform.com/to/vb9WAT6w',
  },
  integrationsUrl: 'https://timelyapp.com/integrations',
  questLaunched: '2017-06-11',
  auditsLaunched: '2020-08-31',
  zIndex: {
    // mirrored in variables.scss
    modalDefault: 20,
    mainNav: 1001,
  },
  disableBatch: true,
  emojiCdnPath: 'https://emoji.memorycdn.com/tw64',
  defaultTagEmoji: '1f3f7-fe0f.png',
  defaultAvatar: '/assets/thumbs/user_medium_retina.jpg',
  defaultTeamEmoji: '1f465.png',
  pusherAppKeys: {
    production: '4eb740fd03fc7f8a7b9d',
    staging: '913079108b075c84e8c5',
    // Local-dev shared Pusher app (app id 1331647). Public client key — matches
    // Rails `config/credentials/development.yml.enc` (`pusher_key`) and TIC
    // `apps/integration-cloud/.env` (`PUSHER_KEY`).
    local: '55d14d76baf8d1a878ea',
  },
  pusherCluster: 'eu',
  idleAfterMinutes: 10,
  minimumBrowserVersion: {
    safari: 10.1,
    chrome: 57,
    electron: 4,
    firefox: 68,
    opera: 44,
    edge: 17,
    timelyApp: 0.5,
    vivaldi: 1.8,
  },
  termsOfServiceUrl: 'https://memory.ai/terms-of-service',
  privacyUrl: 'https://memory.ai/privacy-policy',
  gdprUrl: 'https://memory.ai/data-security-gdpr',
  projects: {
    loadFullyLimit: 300,
    loadLightLimit: 4000,
    numRecentProjectsToFetch: 250,
    numArchivedProjectsToFetch: 20,
    numProjectSearchResults: 50,
    projectsPerPage: 50, // Hard coded limit on server, do not increase
  },
  isPublicReport: !!globalThis.document?.getElementById?.('public-app'),
  /** Actual width of scrollbars in the current browser. */
  scrollbarWidth: (() => {
    try {
      const outer = globalThis.document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.overflow = 'scroll';
      globalThis.document.body.appendChild(outer);

      const inner = globalThis.document.createElement('div');
      outer.appendChild(inner);
      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      outer.parentNode?.removeChild(outer);
      return scrollbarWidth;
    } catch {
      return 0;
    }
  })(),
  /** The initial URL the app was loaded with (before any redirects). */
  initialPathname: globalThis.window?.location.pathname ?? '',
  joinExistingWorkspaceUrl: 'https://support.memory.ai/en/articles/3364976-add-users-in-timely',
  defaultLogo: 'https://d1vbcromo72rmd.cloudfront.net/assets/apps_logo/default.png',
};
